import React, { useState, useEffect, useRef, useMemo } from 'react';
import { scaleLinear, scaleUtc } from 'd3-scale';
import { formatLocale } from 'd3-format'
import { timeWeek } from 'd3-time';
import { extent, max, min } from 'd3-array';
import { line, curveCatmullRom, area } from 'd3-shape'

import './TimelapseBarGraph.scss';

const padding = {
  // top: 64,
  top: 8,
  bottom: 36,
  left: 32,
  right: 84,
};

const defaultText = {
  large: 16,
  medium: 10, // 28, // 12,
  week: 12,
  small: 9,
};
const smallText = {
  large: 14,
  medium: 10, // 28, // 12,
  week: 9,
  small: 9,
};

const minWidth = 640;
const baseWidth = 960;
const baseHeight = 480;
const format = formatLocale('en-US').format;
const formatThousands = format('~s');
let context;

function formatDate(date, language, year = false) {
  return `${date.getMonth() + 1}/${date.getDate()}${year ? `/${date.getFullYear()}` : ''}`
}

export default function TimelapseBarGraph(props) {
  const tooltipData = null;
  const [dimensions, setDimensions] = useState({ width: baseWidth, height: baseHeight, barWidth: 16, scale: 1 });
  const [text, setText] = useState(defaultText);
  const graphRef = useRef();

  const { data, contentWidth, contentHeight, selectedMetric, language, nonZeroBaseline } = props;
  const { metricKey } = selectedMetric;

  useEffect(() => {
    const width = contentWidth >= minWidth ? contentWidth : minWidth;
    const scale = contentWidth >= minWidth ? 1 : contentWidth / minWidth;
    if (width < baseWidth) {
      setText(smallText);
    } else {
      setText(defaultText);
    }
    const height = contentHeight
      ? contentHeight / scale
      : width / baseWidth * baseHeight;
    const dataLength = data.length || 30;
    const barWidth = (contentWidth / dataLength) / 2;
    setDimensions({ width, height, barWidth, scale });
  }, [contentWidth, contentHeight, data.length]);

  const xExtent = useMemo(() => (data.length ? extent(data.map(day => day.date)) : [new Date('Jan 01 2019'), new Date()]), [data]);
  const xScale = useMemo(() => (
    scaleUtc()
      .domain(xExtent)
      .range([padding.left + dimensions.barWidth / 2, dimensions.width - (dimensions.barWidth / 2 + padding.right)])
  ), [xExtent, dimensions.width, dimensions.barWidth]);

  const yBarMin = useMemo(() => min(data.map(day => day[`new_${metricKey}`])), [data, metricKey]);
  const yAverageLineMin = useMemo(() => min(data.map(day => day[`new_${metricKey}_7dayAverage`])), [data, metricKey]);
  const baseline = nonZeroBaseline ? Math.min(yBarMin, yAverageLineMin) : 0
  const yMax = useMemo(() => max(data.map(day => day[`new_${metricKey}`]).concat(1)), [data, metricKey]);

  const yScale = useMemo(() => (
    scaleLinear()
      .domain([baseline, yMax])
      .range([0, yMax ? dimensions.height - (padding.top + padding.bottom) : 0])
      .nice()
  ), [yMax, dimensions.height, baseline]);

  useEffect(() => {
    const canvas = graphRef.current;
    if (canvas) {
      context = canvas.getContext('2d');
      context.clearRect(0, 0, dimensions.width, dimensions.height);
      context.lineWidth = 1 / dimensions.scale;

      // render x ticks
      const maxLabelWidth = 26 / dimensions.scale; // 26;
      context.strokeStyle = '#707070';
      context.fillStyle = '#4C4C4C';
      context.textAlign = 'center';
      context.font = `${Math.ceil(text.medium / dimensions.scale)}px sans-serif`;
      const xExtent = xScale.domain();
      const lineHeight = padding.bottom / 4;
      [
        ...new Set(
          timeWeek.every(Math.max(1, Math.floor(data.length / 14))).range(...xExtent)
            .filter(date => {
              const xPos = xScale(date);
              if (xPos - maxLabelWidth < xScale(xExtent[0])) {
                return false;
              }
              if (xPos + maxLabelWidth > xScale(xExtent[1])) {
                return false;
              }
              return true;
            })
            .concat(xExtent)
        )
      ].forEach(xt => {
        const formatedDate = formatDate(xt, language);
        const x = xScale(xt)
        context.beginPath();
        context.moveTo(x, dimensions.height - padding.bottom);
        context.lineTo(x, dimensions.height - padding.bottom + lineHeight);
        context.stroke();
        context.fillText(formatedDate, x, dimensions.height - 2);
      });

      // render y ticks
      context.textAlign = 'left';
      yScale.ticks(3).filter(yt => Number.isInteger(yt))
        .forEach(yt => {
          const lineStroke = yt === 0 ? '#707070' : '#C4C4C4';
          context.strokeStyle = lineStroke;
          context.beginPath();
          const y = dimensions.height - padding.bottom - yScale(yt);
          context.moveTo(padding.left, y);
          context.lineTo(dimensions.width - padding.right, y);
          context.stroke();
          context.fillText(formatThousands(yt), dimensions.width - (padding.right - 8), y);
        });

      // render bars
      context.fillStyle = props.barColor;
      data.forEach(day => {
        const value = yScale(day[`new_${metricKey}`]);
        const barHeight = Math.max(0, isNaN(value) ? 0 : value);
        const maxBarPadding = 3
        const fullBarWidth = Math.max(1 / dimensions.scale , Math.floor(dimensions.barWidth * 1.8));
        let barPadding = maxBarPadding
        if (fullBarWidth < 6) {
          barPadding = 2
        }
        if (fullBarWidth < 4) {
          barPadding = 1
        }
        if (fullBarWidth < 3) {
          barPadding = 0.5
        }
        const x = xScale(day.date) - ((fullBarWidth + barPadding) / 2);
        const y = (dimensions.height - padding.bottom) - barHeight;
        context.beginPath();
        context.rect(x, y, fullBarWidth - (dimensions.scale > 0.9 ? barPadding : 0), barHeight);
        context.fill();
      });

      // render avg line / area
      const lineGen = line()
        .x(d => xScale(d.date))
        .y(d =>  Math.max(0, dimensions.height - padding.bottom - yScale(d[`new_${metricKey}_7dayAverage`])))
        .curve(curveCatmullRom.alpha(0.5))
        .context(context);

      const areaGen = area()
        .x(d => xScale(d.date))
        .y1(d =>  Math.max(0, dimensions.height - padding.bottom - yScale(d[`new_${metricKey}_7dayAverage`])))
        .y0(dimensions.height - padding.bottom)
        .curve(curveCatmullRom.alpha(0.5))
        .context(context);

      const averageStroke = metricKey === 'cases'
          ? '#883143'
          : '#272828';
      const averageFill = metricKey === 'cases'
          ? 'rgba(136, 49, 67, 0.1)'
          : 'rgba(140, 153, 154, 0.1)';

      context.strokeStyle = averageStroke;
      context.beginPath();
      lineGen(data);
      context.stroke();
      context.fillStyle = averageFill;
      context.beginPath();
      areaGen(data);
      context.fill();

    }
  }, [data, dimensions, language, metricKey, props.barColor, tooltipData, xScale, yScale, text.medium]);

  return (
    <div className='TimelapseBarGraph' style={{ height: dimensions.height }}>
      {
        data.length ? (
          <canvas
            ref={graphRef}
            className='canvas'
            style={{ transform: `scale(${dimensions.scale})`}}
            width={dimensions.width}
            height={dimensions.height}
          />
        ) : null
      }
    </div>
  );
};
